/**
 * useSetupStatus — shared hook that checks onboarding prerequisites,
 * exposes per-feature completion booleans, and returns the context-aware
 * next action for nudge display (RFC-022).
 *
 * Backward-compatible: existing callers without context still work
 * (falls back to default priority ordering).
 */
import { useEffect, useRef, useState } from 'react'
import { settingsService } from '@/services/SettingsService'
import { http } from '@/services/api'
import type { NudgeContext } from '@/hooks/useNudgeContext'

export interface SetupStatus {
  loading: boolean
  hasCompanyProfile: boolean
  hasTargetCompanies: boolean
  hasLinkedIn: boolean
  hasSlack: boolean
  hasEmail: boolean
  hasBrandVoice: boolean
  hasIcp: boolean
  hasTAGConfig: boolean
  hasCrm: boolean
  /** The single most important next action (null when all done) */
  nextAction: NextAction | null
  /** Number of completed onboarding steps */
  completedCount: number
  /** Total number of onboarding steps */
  totalSteps: number
  /** Full ordered step list with completion state — for checklist UIs */
  steps: SetupStep[]
  /** Refresh status (e.g. after user completes a step) */
  refresh: () => void
}

export interface NextAction {
  id: string
  label: string
  description: string
  path: string
}

export interface SetupStep {
  id: string
  label: string
  description: string
  path: string
  completed: boolean
}

// -------------------------------------------------------------------------- //
// Customer-facing activation milestone.
// -------------------------------------------------------------------------- //
// This id MUST match the server's step id. The server owns completion, wording,
// and destination. Connections and ICP are acquired or inferred inside the
// work; they are not first-run chores.
const ACTIONS: Omit<NextAction, 'description'>[] = [
  { id: 'first_operation', label: 'Give Ricald its first outcome', path: '/app/query' },
]

const DESCRIPTIONS: Record<string, string> = {
  // Last-resort copy only. The server supplies these; see the steps builder.
  first_operation: 'Describe the result you want. Ricald starts with what it can access and requests anything missing inside the work',
  // Legacy observations retained for consumers outside the activation card.
  connect_channel: 'Link email, LinkedIn, or your CRM. Everything after this depends on it — Ricald reads signals and acts through the channels you connect',
  configure_icp: 'Industries, company size, buyer titles. Ricald uses this to find and score accounts — adding your own list is optional',
  first_approval: 'Ricald drafts, you decide. Open the queue and approve or reject its first proposed action',
  // Superseded ids, kept so older consumers referencing them still render text.
  import_prospects: 'Type company names or paste a list — Ricald will enrich and start monitoring signals',
  first_outreach: 'Open your queue, pick an account, and send your first message via LinkedIn or email',
  // Legacy keys (backward compat for consumers that use old ids)
  company_profile: 'Add your website so Ricald can understand your business',
  company: 'Add your website so Ricald can understand your business',
  targets: 'Type company names or paste a list — Ricald will enrich and start monitoring signals',
  linkedin: 'Import your connections so Ricald can find warm introductions into target accounts',
  slack: 'Connect Slack so Ricald can deliver deal briefings and surge alerts to your team',
  email: 'Set up email so Ricald can send outreach on your behalf',
  invite_team: 'Add sales reps and managers so they can receive deal briefings',
  connect_crm: 'Link Salesforce or HubSpot so Ricald can sync contacts',
  configure_tag: 'Adjust how Ricald weighs engagement and timing signals',
  first_query: 'Try the AI assistant — ask about your prospects or get help drafting outreach',
}

// Map server step ids to legacy ids used by existing consumers (Dashboard, TAG, Prospects)
const LEGACY_ID_MAP: Record<string, string> = {
  company_profile: 'company',
  import_prospects: 'targets',
}

// -------------------------------------------------------------------------- //
// Page affinity boost maps (client-side fallback when server is unavailable)
// -------------------------------------------------------------------------- //
function getPageBoosts(page: string, params: Record<string, string>): Record<string, number> {
  if (page.includes('/tag'))         return { import_prospects: 1, configure_tag: 0.8 }
  if (page.includes('/prospects'))   return { import_prospects: 1, configure_icp: 0.8 }
  if (page.includes('/query'))       return { company_profile: 1, import_prospects: 0.8, first_query: 0.6 }
  if (page.includes('/deal-briefings')) return { import_prospects: 1, connect_crm: 0.5 }
  if (page.includes('/conversation')) return { connect_crm: 0.5 }
  if (page.includes('/outreach'))    return { connect_crm: 0.8, company_profile: 0.5 }
  if (page.includes('/ads'))         return { import_prospects: 0.8 }
  if (page.includes('/analytics'))   return { import_prospects: 0.8, configure_icp: 0.5 }

  if (page.includes('/settings')) {
    const tab = params.tab
    if (tab === 'integrations') return { connect_crm: 1 }
    if (tab === 'icp')          return { configure_icp: 1 }
    if (tab === 'tag')          return { configure_tag: 1 }
    if (tab === 'team')         return { invite_team: 1 }
    return { company_profile: 1 }
  }

  return {}
}

function getUtmBoosts(params: Record<string, string>): Record<string, number> {
  const source = params.utm_source ?? params.from ?? ''
  if (['salesforce', 'hubspot', 'crm'].includes(source)) return { connect_crm: 1 }
  if (source === 'linkedin') return { import_prospects: 0.8 }
  if (['import', 'csv'].includes(source)) return { import_prospects: 1 }
  return {}
}

function getGoalBoosts(goal: string | null): Record<string, number> {
  switch (goal) {
    case 'find_buyers':
      return { configure_icp: 1, import_prospects: 0.9, configure_tag: 0.7, connect_crm: 0.5 }
    case 'linkedin_presence':
      return { company_profile: 0.8 }
    case 'market_research':
      return { import_prospects: 1, configure_tag: 0.8 }
    default:
      return {}
  }
}

/**
 * Reorder nudge actions so the most relevant incomplete step comes first.
 * Client-side fallback used when server endpoint is not available.
 */
function reorderForContext(
  actions: Omit<NextAction, 'description'>[],
  context: NudgeContext,
  completionMap: Record<string, boolean>,
): Omit<NextAction, 'description'>[] {
  const incomplete = actions.filter(a => !completionMap[a.id])
  if (incomplete.length === 0) return []

  const pageBoosts = getPageBoosts(context.page, context.params)
  const utmBoosts = getUtmBoosts(context.params)
  const goalBoosts = getGoalBoosts(context.goal)

  const scored = incomplete.map(action => ({
    action,
    boost: (pageBoosts[action.id] ?? 0) * 100
         + (utmBoosts[action.id] ?? 0) * 10
         + (goalBoosts[action.id] ?? 0) * 1,
  }))

  scored.sort((a, b) => b.boost - a.boost)
  return scored.map(s => s.action)
}

// -------------------------------------------------------------------------- //
// Server-side onboarding status response types
// -------------------------------------------------------------------------- //
interface OnboardingStep {
  id: string
  title: string
  description: string
  completed: boolean
  completed_at: string | null
  priority: number
  href: string
}

interface OnboardingStatusResponse {
  steps: OnboardingStep[]
  completed_count: number
  total_steps: number
  all_complete: boolean
}

interface OnboardingStateResponse {
  steps: Record<string, { completed_at: string; completed_by: string } | null>
}

export function useSetupStatus(context?: NudgeContext): SetupStatus {
  const [loading, setLoading] = useState(true)
  const [status, setStatus] = useState({
    hasCompanyProfile: false,
    hasTargetCompanies: false,
    hasLinkedIn: false,
    hasSlack: false,
    hasEmail: false,
    hasBrandVoice: false,
    hasIcp: false,
    hasTAGConfig: false,
    hasCrm: false,
  })
  const [serverSteps, setServerSteps] = useState<OnboardingStep[] | null>(null)
  const [tick, setTick] = useState(0)
  const prevCompletionRef = useRef<Record<string, boolean>>({})

  // Load server-side progress to seed prevCompletionRef (prevents re-notifications)
  useEffect(() => {
    const loadServerProgress = async () => {
      try {
        const state = await http<OnboardingStateResponse>('/onboarding/state')
        prevCompletionRef.current = Object.fromEntries(
          Object.entries(state.steps).map(([k, v]) => [k, v !== null])
        )
      } catch {
        // Fall back to empty — duplicate notifications are harmless (server is idempotent)
      }
    }
    loadServerProgress()
  }, [])

  useEffect(() => {
    let cancelled = false

    const check = async () => {
      setLoading(true)

      // Build query params for server-side context-aware ordering
      const queryParams: Record<string, string> = {}
      if (context?.page) queryParams.current_page = context.page
      if (context?.params?.utm_source) queryParams.utm_source = context.params.utm_source
      if (context?.params?.utm_campaign) queryParams.utm_campaign = context.params.utm_campaign
      if (context?.goal) queryParams.tenant_goal = context.goal
      if (context?.params?.tab) queryParams.tab = context.params.tab

      // Fetch all data in parallel: legacy checks + server-driven ordering
      const [orgResult, linkedinResult, tagResult, slackResult, serverResult] = await Promise.allSettled([
        settingsService.getOrgSettings(),
        http<{ logged_in: boolean }>('/linkedin-session/status'),
        http<{ total_accounts: number }>('/tag/stats'),
        http<{ connected: boolean }>('/integrations/slack/connection'),
        http<OnboardingStatusResponse>('/onboarding/status', { query: queryParams }),
      ])

      if (cancelled) return

      const org = orgResult.status === 'fulfilled' ? orgResult.value : null
      const linkedin = linkedinResult.status === 'fulfilled' ? linkedinResult.value : null
      const tagStats = tagResult.status === 'fulfilled' ? tagResult.value : null
      const slack = slackResult.status === 'fulfilled' ? slackResult.value : null
      const serverStatus = serverResult.status === 'fulfilled' ? serverResult.value : null

      // Update server steps if available
      if (serverStatus) {
        setServerSteps(serverStatus.steps)
      }

      setStatus({
        hasCompanyProfile: !!(org?.company_name && org.company_name.trim() && org?.website && org.website.trim()),
        hasTargetCompanies: !!(tagStats && tagStats.total_accounts > 0),
        hasLinkedIn: !!(linkedin?.logged_in),
        hasSlack: !!(slack?.connected),
        hasEmail: !!(org?.from_email && org.from_email.trim()),
        hasBrandVoice: !!(org?.brand_voice_settings?.tone),
        hasIcp: serverStatus?.steps?.find(s => s.id === 'configure_icp')?.completed ?? false,
        hasTAGConfig: serverStatus?.steps?.find(s => s.id === 'configure_tag')?.completed ?? false,
        hasCrm: serverStatus?.steps?.find(s => s.id === 'connect_crm')?.completed ?? false,
      })
      setLoading(false)
    }

    check()
    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick, context?.page, context?.goal])

  // Build legacy completion observations used by older nudge consumers.
  // Used ONLY for nudge ordering and the /onboarding/complete notification —
  // never for what the checklist renders (ADR-0041). `first_operation` is
  // deliberately absent: the server observes canonical Operation state.
  const completionMap: Record<string, boolean> = {
    configure_icp: status.hasIcp,
    import_prospects: status.hasTargetCompanies,
  }

  // Merge server-side completions (server is source of truth)
  if (serverSteps) {
    for (const step of serverSteps) {
      if (step.completed) {
        completionMap[step.id] = true
      }
    }
  }

  // Notify server of newly-completed steps (fire-and-forget, idempotent)
  useEffect(() => {
    for (const [stepId, isComplete] of Object.entries(completionMap)) {
      if (isComplete && !prevCompletionRef.current[stepId]) {
        http(`/onboarding/complete/${stepId}`, { method: 'POST' }).catch(() => {})
      }
    }
    prevCompletionRef.current = { ...completionMap }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(completionMap)])

  // Determine next action — prefer server ordering, fall back to client
  let nextAction: NextAction | null = null
  // Only count completions for the surfaced activation ACTIONS — server merge
  // can add unrelated keys (company_profile, first_query) that would inflate the count.
  // Checklist steps are RENDERED from server state, never derived here (ADR-0041).
  //
  // The client used to build this list from its own completion map, which is how
  // a step ended up hard-coded `false` against a server event nobody had
  // implemented: the step could never complete, so the card asked indefinitely
  // for work already done. Completion, wording and destination now all come from
  // the server, which owns them and can actually observe them.
  //
  // The client still chooses WHICH steps to surface and in what order — that is
  // presentation, and the server's list also carries RFC-022 steps this card
  // does not show.
  //
  // When the server has not answered, the list is EMPTY rather than guessed.
  // Showing a checklist assembled from client-side inference is how the
  // never-completing step survived unnoticed for so long; an absent card is
  // honest, a wrong one is not. OnboardingChecklist renders nothing for [].
  const serverById = new Map((serverSteps ?? []).map(s => [s.id, s]))
  const steps: SetupStep[] = ACTIONS.flatMap(action => {
    const fromServer = serverById.get(action.id)
    if (!fromServer) return []
    return [{
      id: LEGACY_ID_MAP[action.id] ?? action.id,
      // Server copy wins; the local strings remain only as a last resort for a
      // step the server declares without descriptive text.
      label: fromServer.title || action.label,
      description: fromServer.description || DESCRIPTIONS[action.id] || '',
      path: fromServer.href || action.path,
      completed: fromServer.completed,
    }]
  })

  // Counted from what is actually rendered, so the progress line can never
  // disagree with the list beneath it.
  const completedCount = steps.filter(s => s.completed).length
  const totalSteps = steps.length

  // Ignore server-side legacy observations that are not activation milestones.
  const validStepIds = new Set(ACTIONS.map(a => a.id))

  if (serverSteps) {
    // Filter server steps to only the customer-facing activation milestone.
    const nextStep = serverSteps.find(s => !s.completed && validStepIds.has(s.id))
    if (nextStep) {
      nextAction = {
        id: LEGACY_ID_MAP[nextStep.id] ?? nextStep.id,
        label: nextStep.title,
        description: nextStep.description,
        path: nextStep.href,
      }
    }
  }
  if (!nextAction) {
    // Client-side fallback when server endpoint is not available
    const ctx = context ?? { page: '', params: {}, goal: null }
    const prioritized = reorderForContext(ACTIONS, ctx, completionMap)
    const first = prioritized[0]
    if (first) {
      nextAction = {
        id: LEGACY_ID_MAP[first.id] ?? first.id,
        label: first.label,
        description: DESCRIPTIONS[first.id],
        path: first.path,
      }
    }
  }


  return {
    loading,
    ...status,
    nextAction,
    completedCount,
    totalSteps,
    steps,
    refresh: () => setTick(t => t + 1),
  }
}
